import { create } from "zustand";
import type {
  Product,
  Initiative,
  Classification,
  Status,
  Tag,
  User,
  Quarter,
  FieldConfig,
  LabsFeature,
  FilterState,
  ViewMode,
} from "@/types";
import type { StatsResponse, RoadmapHealthResponse, AiUsage, SavedView } from "@/services/api";
import { viewApi } from "@/services/api";

export interface AppNotification {
  id: number; // unique per call so the banner re-renders / re-arms its timer
  type: 'error' | 'success' | 'info';
  message: string;
}

interface AppState {
  // Data
  products: Product[];
  initiatives: Initiative[];
  classifications: Classification[];
  statuses: Status[];
  tags: Tag[];
  owners: User[];
  quarters: Quarter[];
  fieldConfigs: FieldConfig[];
  labsFeatures: LabsFeature[];
  aiProviderLabel: string;
  aiProviderModel: string;
  aiUsage: AiUsage | null;

  // Dashboard stats cache
  dashboardStats: StatsResponse | null;
  dashboardStatsLoading: boolean;

  // Intelligence cache (persists within session)
  intelligenceHealth: RoadmapHealthResponse | null;
  intelligenceMessages: Array<{ role: 'user' | 'assistant'; content: string }>;
  intelligenceDigest: { output: string; format: 'slack' | 'executive' | 'team'; daysBack: number } | null;

  // Notification state
  unreadNotificationCount: number;

  // Global toast notification (error/success/info banner)
  notification: AppNotification | null;

  // Saved (named) views
  accessibleViews: SavedView[];
  activeSavedView: SavedView | null;
  accessibleViewsLoaded: boolean;
  // When true, the Navbar's "auto-load favorite on landing" effect is suppressed once — set by the
  // ViewSwitcher when the user explicitly picks a view (or Default) from the dropdown so their
  // choice wins over the pinned favorite.
  skipFavoriteLoad: boolean;

  // UI State
  viewMode: ViewMode;
  filters: FilterState;
  isLoading: boolean;
  error: string | null;
  selectedInitiativeId: string | null;

  // Global Modal State
  initiativeModalOpen: boolean;
  initiativeModalMode: 'add' | 'edit' | 'view';
  selectedInitiative: Initiative | null;
  initialModalTab: 'main' | 'dependencies' | 'comments' | 'audit' | 'intelligence' | null;
  refreshTrigger: number; // Increment to trigger data refresh
  savedFlashToken: number; // Increment to flash a "Saved" confirmation (see SavedToast)

  // Actions
  setProducts: (products: Product[]) => void;
  setInitiatives: (initiatives: Initiative[]) => void;
  setClassifications: (classifications: Classification[]) => void;
  setStatuses: (statuses: Status[]) => void;
  setTags: (tags: Tag[]) => void;
  setOwners: (owners: User[]) => void;
  setQuarters: (quarters: Quarter[]) => void;
  setFieldConfigs: (configs: FieldConfig[]) => void;
  setLabsFeatures: (features: LabsFeature[]) => void;
  setAiProvider: (label: string, model: string) => void;
  setAiUsage: (usage: AiUsage | null) => void;
  setViewMode: (mode: ViewMode) => void;
  setFilters: (filters: Partial<FilterState>) => void;
  resetFilters: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedInitiativeId: (id: string | null) => void;

  // Dashboard Actions
  setDashboardStats: (stats: StatsResponse | null) => void;
  setDashboardStatsLoading: (loading: boolean) => void;

  // Intelligence Actions
  setIntelligenceHealth: (data: RoadmapHealthResponse | null) => void;
  setIntelligenceMessages: (msgs: Array<{ role: 'user' | 'assistant'; content: string }>) => void;
  setIntelligenceDigest: (digest: { output: string; format: 'slack' | 'executive' | 'team'; daysBack: number } | null) => void;

  // Notification Actions
  setUnreadNotificationCount: (count: number) => void;

  // Toast notification Actions
  notify: (type: AppNotification['type'], message: string) => void;
  dismissNotification: () => void;

  // Saved (named) views Actions
  setAccessibleViews: (views: SavedView[]) => void;
  setActiveSavedView: (view: SavedView | null) => void;
  setSkipFavoriteLoad: (v: boolean) => void;
  loadAccessibleViews: () => Promise<void>;
  resetViews: () => void;

  // Modal Actions
  openAddInitiativeModal: () => void;
  openViewInitiativeModal: (initiative: Initiative, initialTab?: 'main' | 'dependencies' | 'comments' | 'audit' | 'intelligence') => void;
  openEditInitiativeModal: (initiative: Initiative) => void;
  closeInitiativeModal: () => void;
  triggerRefresh: () => void;
  flashSaved: () => void;

  // Initiative Updates (in-memory, no API call)
  updateInitiativeCommentCount: (initiativeId: string, count: number) => void;
  updateInitiativeStarred: (initiativeId: string, isStarred: boolean) => void;
}

const DEFAULT_FILTERS: FilterState = {
  fiscalYears: ["FY2026"],
  owners: [],
  tags: [],
  statuses: [],
  classifications: [],
  searchQuery: "",
};

export const useAppStore = create<AppState>((set) => ({
  // Initial state
  products: [],
  initiatives: [],
  classifications: [],
  statuses: [],
  tags: [],
  owners: [],
  quarters: [],
  fieldConfigs: [],
  labsFeatures: [],
  aiProviderLabel: 'Claude',
  aiProviderModel: 'claude-haiku-4-5-20251001',
  aiUsage: null,
  dashboardStats: null,
  dashboardStatsLoading: false,
  intelligenceHealth: null,
  intelligenceMessages: [],
  intelligenceDigest: null,
  unreadNotificationCount: 0,
  notification: null,
  accessibleViews: [],
  activeSavedView: null,
  accessibleViewsLoaded: false,
  skipFavoriteLoad: false,
  viewMode: "timeline",
  filters: DEFAULT_FILTERS,
  isLoading: false,
  error: null,
  selectedInitiativeId: null,

  // Global Modal State
  initiativeModalOpen: false,
  initiativeModalMode: 'add',
  selectedInitiative: null,
  initialModalTab: null,
  refreshTrigger: 0,
  savedFlashToken: 0,

  // Actions
  setProducts: (products) => set({ products }),
  setInitiatives: (initiatives) => set({ initiatives }),
  setClassifications: (classifications) => set({ classifications }),
  setStatuses: (statuses) => set({ statuses }),
  setTags: (tags) => set({ tags }),
  setOwners: (owners) => set({ owners }),
  setQuarters: (quarters) => set({ quarters }),
  setFieldConfigs: (fieldConfigs) => set({ fieldConfigs }),
  setLabsFeatures: (labsFeatures) => set({ labsFeatures }),
  setAiProvider: (aiProviderLabel, aiProviderModel) => set({ aiProviderLabel, aiProviderModel }),
  setAiUsage: (aiUsage) => set({ aiUsage }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    })),
  resetFilters: () => set({ filters: DEFAULT_FILTERS }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setSelectedInitiativeId: (id) => set({ selectedInitiativeId: id }),

  // Dashboard Actions
  setDashboardStats: (stats) => set({ dashboardStats: stats }),
  setDashboardStatsLoading: (loading) => set({ dashboardStatsLoading: loading }),

  // Intelligence Actions
  setIntelligenceHealth: (data) => set({ intelligenceHealth: data }),
  setIntelligenceMessages: (msgs) => set({ intelligenceMessages: msgs }),
  setIntelligenceDigest: (digest) => set({ intelligenceDigest: digest }),

  // Notification Actions
  setUnreadNotificationCount: (count) => set({ unreadNotificationCount: count }),

  // Toast notification Actions
  notify: (type, message) => set({ notification: { id: Date.now(), type, message } }),
  dismissNotification: () => set({ notification: null }),

  // Saved (named) views Actions
  setAccessibleViews: (views) => set({ accessibleViews: views }),
  setActiveSavedView: (view) => set({ activeSavedView: view }),
  setSkipFavoriteLoad: (v) => set({ skipFavoriteLoad: v }),
  loadAccessibleViews: async () => {
    try {
      const views = await viewApi.getAll();
      set({ accessibleViews: views, accessibleViewsLoaded: true });
    } catch (err) {
      console.warn('Failed to load saved views:', err);
      set({ accessibleViewsLoaded: true });
    }
  },
  // Clear all saved-view session state — called on login/logout so one user's
  // active/pinned view never leaks into another user's session on the same tab.
  resetViews: () => set({ activeSavedView: null, accessibleViews: [], accessibleViewsLoaded: false }),

  // Modal Actions
  openAddInitiativeModal: () =>
    set({
      initiativeModalOpen: true,
      initiativeModalMode: 'add',
      selectedInitiative: null,
    }),
  openViewInitiativeModal: (initiative, initialTab) =>
    set({
      initiativeModalOpen: true,
      initiativeModalMode: 'view',
      selectedInitiative: initiative,
      initialModalTab: initialTab || null,
    }),
  openEditInitiativeModal: (initiative) =>
    set({
      initiativeModalOpen: true,
      initiativeModalMode: 'edit',
      selectedInitiative: initiative,
    }),
  closeInitiativeModal: () =>
    set({
      initiativeModalOpen: false,
      selectedInitiative: null,
      initialModalTab: null,
    }),
  triggerRefresh: () =>
    set((state) => ({
      refreshTrigger: state.refreshTrigger + 1,
    })),
  flashSaved: () =>
    set((state) => ({
      savedFlashToken: state.savedFlashToken + 1,
    })),

  // Update comment count for a specific initiative (in-memory, no API call)
  updateInitiativeCommentCount: (initiativeId, count) =>
    set((state) => ({
      initiatives: state.initiatives.map((init) =>
        init.id === initiativeId
          ? { ...init, _count: { ...(init as any)._count, comments: count } }
          : init
      ),
    })),

  updateInitiativeStarred: (initiativeId, isStarred) =>
    set((state) => ({
      initiatives: state.initiatives.map((init) =>
        init.id === initiativeId ? { ...init, isStarred } : init
      ),
    })),
}));
